// Segment shape functions. Only +, -, * and / are used (no Math.sin / Math.pow), so
// terrain heights are bit-identical on every platform, as Rapier's deterministic build requires.

import type { SegmentKind } from '../../config/environments';

export interface Segment {
  kind: SegmentKind;
  x0: number;
  length: number;
  /** Height at x0. */
  y0: number;
  /** Primary amplitude: bump height, climb rise (negative for drops), kicker lip height. */
  a: number;
  /** Secondary parameter: kicker drop depth below the lip. */
  b: number;
  /** Repeat count for rollers and washboards; kicker rise fraction ×100 otherwise. */
  n: number;
}

/** Smootherstep: zero slope and curvature at both ends; peak slope 1.875 at t = 0.5. */
export function smoother(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

/** Quartic bump: 0 at t=0 and t=1 with zero slope, peak 1 at t=0.5; peak slope ≈ 3.08. */
export function bump(t: number): number {
  const u = t * (1 - t);
  return 16 * u * u;
}

export const SMOOTHER_PEAK_SLOPE = 1.875;
export const BUMP_PEAK_SLOPE = 3.0792;

/** Height change from the start of the segment to its end. */
export function segmentRise(seg: Segment): number {
  switch (seg.kind) {
    case 'climb':
    case 'drop':
      return seg.a;
    case 'kicker':
      return seg.a - seg.b;
    default:
      return 0;
  }
}

/** Height offset relative to seg.y0 at normalised position t ∈ [0, 1]. */
export function segmentOffset(seg: Segment, t: number): number {
  switch (seg.kind) {
    case 'flat':
      return 0;
    case 'climb':
    case 'drop':
      return seg.a * smoother(t);
    case 'crest':
      return seg.a * bump(t);
    case 'rollers':
    case 'washboard': {
      const k = t * seg.n;
      const local = k - Math.floor(k);
      return seg.a * bump(local);
    }
    case 'kicker': {
      // Rise along a cubic ramp that steepens into a sharp lip (end slope 3a/riseLength), then fall to the landing.
      const riseFrac = seg.n / 100;
      if (t <= riseFrac) {
        const u = t / riseFrac;
        return seg.a * u * u * u;
      }
      const v = (t - riseFrac) / (1 - riseFrac);
      return seg.a - seg.b * smoother(v);
    }
  }
}
